<template>
  <div class="title-slot">
    <div class="title-slot__left float-left">
      <div
        v-if="showDot"
        class="title-slot__dot"
      >
        <DottedDropdown :id="`workpermit-collapse-title_${id}`">
          <li
            v-for="(item, index) in dotOption"
            :key="index"
          >
            <a
              class="dot-option-btn"
              @click="() => item.operate && item.operate(permitData)"
            >
              {{ item.title }}
            </a>
          </li>
        </DottedDropdown>
      </div>
      <div class="title-slot__box">
        <div class="title-slot__code">
          # {{ innerData.code || '--' }}
        </div>
        <div class="title-slot__title">
          <template v-if="!title">
            <strong>
              {{ displayTitle }} -
            </strong>
            <span>
              ({{ innerData.startDate | formatDate('DD MMM YY HH:mm') }} - {{ innerData.endDate | formatDate('DD MMM YY HH:mm') }})
            </span>
          </template>
          <strong v-else>{{ title }}</strong>
        </div>
      </div>
    </div>
    <div
      v-if="showStatus"
      class="pointer title-slot__right"
      @click="openLifecycleModal()"
    >
      <div :class="statusStyle">
        {{ formatStatus | customStatusLabel }}
      </div>
      <Lifecycle
        ref="workPermitLifecycle"
        :modalId="`work-permit-modules-${id}-lifecycle-modal`"
        :commentAttr="lifecycleCommentAttrs.comments"
        :htmlCommentAttr="WP_LIFECYCLE_COMMENT_HTML_KEY"
        :histories="histories || []"
        :type="'workPermit'"
        :allowPreformattedComments="true"
      />
    </div>
  </div>
</template>

<script>
import lodash from 'lodash'
import { mapGetters } from 'vuex'
import DottedDropdown from '@/controls/DottedDropdown'
import Lifecycle from '@/modules/Lifecycle'
import { getAttachments } from '@/api/docs/request'
import { lifecycleCommentAttrs } from '@/utils/consts'
import { formatWorkPermitHistoriesForViewer, getWorkPermitHistoryRowStatus, maskStatusForExternalUser, WP_LIFECYCLE_COMMENT_HTML_KEY } from './utils'
import { WORK_PERMIT_TYPES, WORK_PERMIT_STATUS } from './consts'
import { getRegionalWorkPermitLabel } from './regionalWorkPermitLabels'

export default {
  name: 'CollapseTitleSlot',
  components: {
    DottedDropdown,
    Lifecycle,
  },
  props: {
    permitData: Object,
    title: String,
    showStatus: {
      type: Boolean,
      default: true,
    },
    dotOption: {
      type: Array,
      default: () => [],
    },
    id: {
      type: String,
      default: '',
    },
    showDot: {
      type: Boolean,
      default: false,
    },
  },
  data () {
    return {
      emptyValue: '--',
      lifecycleCommentAttrs,
      rawHistoriesWithDoc: [],
      WP_LIFECYCLE_COMMENT_HTML_KEY,
    }
  },
  computed: {
    ...mapGetters('user', ['isEngineer']),
    ...mapGetters('workPermit', ['currentWorkPermit']),
    displayTitle () {
      const isHoisting = this.innerData.type === WORK_PERMIT_TYPES.HOISTING
      const permit = isHoisting ? getRegionalWorkPermitLabel('hoisting') : this.$t('workPermit.title')
      let companyName = deep(this.innerData, 'contractor.name') || this.emptyValue
      return `${isHoisting ? '- ' : ''}${this.$t('xxOfxx', [permit, companyName])}`
    },
    innerData () {
      return Object.assign({}, this.permitData)
    },

    formatStatus () {
      let status = this.isEngineer ? this.innerData.status : maskStatusForExternalUser(this.innerData.status)
      return status || this.$t('emptyValue')
    },
    statusStyle () {
      if (this.innerData.status) {
        let status = this.innerData.status === WORK_PERMIT_STATUS.INPROGRESS ? 'in-progress' : this.formatStatus
        return `workpermit-status-${status.toLowerCase()} workpermit-status float-right`
      }
      return null
    },
    histories () {
      return formatWorkPermitHistoriesForViewer(this.rawHistoriesWithDoc, {
        isEngineer: this.isEngineer,
        permitType: this.innerData.type,
      })
    },
  },
  methods: {
    async openLifecycleModal () {
      this.rawHistoriesWithDoc = await this.getHistoryWithDocs()
      this.$refs.workPermitLifecycle.init()
    },
    async getHistoryWithDocs () {
      let rawHistories = lodash.cloneDeep(this.innerData.histories) || []
      await Promise.all(rawHistories.map(async (val) => {
        let docs = []
        if (getWorkPermitHistoryRowStatus(val) === WORK_PERMIT_STATUS.SUSPENDED) {
          if (val.detail.docSourceKey) {
            docs = await getAttachments(val.detail.docSourceKey)
            Object.assign(val, {docs})
          }
        }
      }))
      return rawHistories
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~styles/tender/common';

.title-slot {
  display: flex;
  justify-content: space-between;
  &__left {
    display: flex;
  }
  &__right {
    display: flex;
    align-items: center;
    margin-right: 18px;
  }
}
</style>
